import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowUpRight } from 'lucide-react';
import { money } from '../money';
import { PRESETS, isNonprofit } from '../reportCatalog';

// Business Snapshot — legacy-style dashboard (6 cards, 2-col). Each card owns its
// period dropdown (default This year-to-date) and a Go-to-report link. All data
// from the report engine at the company basis. No hardcoded names/figures.
const COLORS = ['#059669', '#0ea5e9', '#f59e0b', '#8b5cf6', '#ef4444', '#14b8a6', '#ec4899', '#64748b', '#84cc16', '#f97316'];
const CARD = 'bg-white text-slate-800 border-slate-200';
const DEFAULT_PRESET = 'this_year_to_date';
const d100 = (c) => (c || 0) / 100;

function shiftYear(iso, n) {
  if (!iso) return iso;
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${Number(y) + n}-${m}-${d}`;
}

function CardShell({ title, preset, setPreset, onGo, goLabel, children, testid }) {
  return (
    <div className={`rounded-xl border p-4 ${CARD} flex flex-col`} data-testid={testid}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <h3 className="text-base font-semibold tracking-tight">{title}</h3>
        <Select value={preset} onValueChange={setPreset}>
          <SelectTrigger className="h-8 w-40 text-xs" data-testid={`${testid}-period`}><SelectValue /></SelectTrigger>
          <SelectContent>{PRESETS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="flex-1 min-h-[180px]">{children}</div>
      <button className="mt-3 inline-flex items-center gap-1 text-sm text-emerald-700 hover:underline self-start" onClick={onGo} data-testid={`${testid}-goto`}>
        {goLabel}<ArrowUpRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function usePnl(base, preset, basis, columns) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let dead = false;
    axios.post(`${base}/reports/run`, { report: 'profit_loss', params: { preset, basis, columns } })
      .then((r) => { if (!dead) setData(r.data); }).catch(() => { if (!dead) setData({ rows: [] }); });
    return () => { dead = true; };
  }, [base, preset, basis, columns]);
  return data;
}

const INCOME_TYPES = ['Income', 'Other Income'];
const EXPENSE_TYPES = ['Expense', 'Other Expense', 'Cost of Goods Sold'];

function Donut({ rows, empty }) {
  const data = (rows || []).filter((r) => r.amount).map((r) => ({ name: r.name, value: Math.abs(r.amount) }));
  const total = (rows || []).reduce((s, r) => s + (r.amount || 0), 0);
  if (!data.length) return <Empty text={empty} />;
  return (
    <div className="flex items-center gap-4">
      <div className="relative" style={{ width: 150, height: 150 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" innerRadius={48} outerRadius={70} paddingAngle={1} stroke="none">
              {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(v) => money(v)} />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-[10px] uppercase text-slate-400">Total</span>
          <span className="text-sm font-semibold tabular-nums">{money(total)}</span>
        </div>
      </div>
      <ul className="flex-1 space-y-1 text-xs max-h-[150px] overflow-auto">
        {data.map((r, i) => (
          <li key={r.name} className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5 truncate"><span className="inline-block h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: COLORS[i % COLORS.length] }} /><span className="truncate">{r.name}</span></span>
            <span className="tabular-nums text-slate-500">{money(r.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Empty({ text }) {
  return <div className="h-full flex items-center justify-center text-sm text-slate-400" data-testid="snapshot-empty">{text || "Your selection doesn't have any info."}</div>;
}

function ComparisonChart({ base, preset, basis, types }) {
  const [series, setSeries] = useState(null);
  useEffect(() => {
    let dead = false;
    (async () => {
      const cur = await axios.post(`${base}/reports/run`, { report: 'profit_loss', params: { preset, basis, columns: 'month' } }).then((r) => r.data).catch(() => null);
      if (!cur || dead) { if (!dead) setSeries([]); return; }
      const labels = cur.columns || [];
      const sumByCol = (rep) => (labels.map((_, i) => (rep.rows || []).filter((r) => types.includes(r.type)).reduce((s, r) => s + (r.amounts?.[i] || 0), 0)));
      const curSeries = sumByCol(cur);
      const prior = await axios.post(`${base}/reports/run`, { report: 'profit_loss', params: { preset: 'custom', start: shiftYear(cur.start, -1), end: shiftYear(cur.end, -1), basis, columns: 'month' } }).then((r) => r.data).catch(() => null);
      const priSeries = prior ? sumByCol(prior) : labels.map(() => 0);
      const curYear = (cur.start || '').slice(0, 4);
      const data = labels.map((lbl, i) => ({ month: lbl, This: d100(curSeries[i]), Prior: d100(priSeries[i]) }));
      if (!dead) setSeries({ data, curYear });
    })();
    return () => { dead = true; };
  }, [base, preset, basis, JSON.stringify(types)]);
  if (!series) return <Empty text="Loading…" />;
  if (!series.data || !series.data.some((d) => d.This || d.Prior)) return <Empty text="Your selection doesn't have any info." />;
  const thisLbl = series.curYear ? `${series.curYear}` : 'This period';
  const priorLbl = series.curYear ? `${Number(series.curYear) - 1}` : 'Prior period';
  return (
    <ResponsiveContainer width="100%" height={180}>
      <LineChart data={series.data} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
        <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} width={44} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
        <Tooltip formatter={(v) => `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} />
        <Line type="monotone" dataKey="This" name={thisLbl} stroke="#059669" strokeWidth={2} dot={false} />
        <Line type="monotone" dataKey="Prior" name={priorLbl} stroke="#94a3b8" strokeWidth={2} strokeDasharray="4 3" dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function AgingMini({ base, report, preset, nounCol }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let dead = false;
    axios.post(`${base}/reports/run`, { report, params: { preset } }).then((r) => { if (!dead) setData(r.data); }).catch(() => { if (!dead) setData({ rows: [] }); });
    return () => { dead = true; };
  }, [base, report, preset]);
  if (!data) return <Empty text="Loading…" />;
  const rows = data.rows || [];
  if (!rows.length) return <Empty text="Your selection doesn't have any info." />;
  const cols = [['current', 'Current'], ['d1_30', '1-30'], ['d31_60', '31-60'], ['d61_90', '61-90'], ['d91_plus', '91 and over'], ['total', 'Total']];
  const t = data.totals || {};
  return (
    <div className="overflow-auto">
      <table className="w-full text-xs">
        <thead><tr className="text-left text-slate-500 border-b border-slate-200">
          <th className="py-1.5 pr-2">{nounCol}</th>{cols.map(([k, l]) => <th key={k} className="py-1.5 px-1.5 text-right">{l}</th>)}
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100">
              <td className="py-1.5 pr-2 truncate max-w-[160px]">{r.name}</td>
              {cols.map(([k]) => <td key={k} className="py-1.5 px-1.5 text-right tabular-nums">{r[k] ? money(r[k]) : '—'}</td>)}
            </tr>
          ))}
          <tr className="font-semibold">
            <td className="py-1.5 pr-2">Total</td>
            {cols.map(([k]) => <td key={k} className="py-1.5 px-1.5 text-right tabular-nums">{money(t[k] || 0)}</td>)}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function BusinessSnapshot({ base, company, go }) {
  const np = isNonprofit(company);
  const basis = company?.accounting_method === 'cash' ? 'cash' : 'accrual';
  const [p1, setP1] = useState(DEFAULT_PRESET);
  const [p2, setP2] = useState(DEFAULT_PRESET);
  const [p3, setP3] = useState(DEFAULT_PRESET);
  const [p4, setP4] = useState(DEFAULT_PRESET);
  const [p5, setP5] = useState(DEFAULT_PRESET);
  const [p6, setP6] = useState(DEFAULT_PRESET);
  const inc = usePnl(base, p1, basis, 'total');
  const exp = usePnl(base, p2, basis, 'total');
  const incomeRows = useMemo(() => (inc?.rows || []).filter((r) => INCOME_TYPES.includes(r.type)).map((r) => ({ name: r.name, amount: r.amounts?.[0] || 0 })), [inc]);
  const expenseRows = useMemo(() => (exp?.rows || []).filter((r) => EXPENSE_TYPES.includes(r.type)).map((r) => ({ name: r.name, amount: r.amounts?.[0] || 0 })), [exp]);
  const goReport = (report) => go('reports', null, { report });
  const incomeNoun = np ? 'Contributions & income' : 'My income';
  return (
    <div className="space-y-4 text-slate-800" data-testid="business-snapshot">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Business Snapshot</h2>
        <span className="text-xs text-slate-500">{basis === 'cash' ? 'Cash' : 'Accrual'} basis</span>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <CardShell testid="snap-income" title={incomeNoun} preset={p1} setPreset={setP1} goLabel="Go to report" onGo={() => goReport(np ? 'profit_loss' : 'profit_loss')}>
          <Donut rows={incomeRows} empty="No income in this period." />
        </CardShell>
        <CardShell testid="snap-expenses" title="My expenses" preset={p2} setPreset={setP2} goLabel="Go to report" onGo={() => goReport('profit_loss')}>
          <Donut rows={expenseRows} empty="No expenses in this period." />
        </CardShell>
        <CardShell testid="snap-income-compare" title="Previous year income comparison" preset={p3} setPreset={setP3} goLabel={`Go to ${np ? 'Statement of Activity' : 'P&L'} comparison`} onGo={() => goReport('profit_loss_prev_year')}>
          <ComparisonChart base={base} preset={p3} basis={basis} types={INCOME_TYPES} />
        </CardShell>
        <CardShell testid="snap-expense-compare" title="Previous year expense comparison" preset={p4} setPreset={setP4} goLabel={`Go to ${np ? 'Statement of Activity' : 'P&L'} comparison`} onGo={() => goReport('profit_loss_prev_year')}>
          <ComparisonChart base={base} preset={p4} basis={basis} types={EXPENSE_TYPES} />
        </CardShell>
        <CardShell testid="snap-who-owes-me" title="Who owes me" preset={p5} setPreset={setP5} goLabel="Go to A/R aging" onGo={() => goReport('ar_aging_summary')}>
          <AgingMini base={base} report="ar_aging_summary" preset={p5} nounCol="Customer" />
        </CardShell>
        <CardShell testid="snap-who-i-owe" title="Who I owe" preset={p6} setPreset={setP6} goLabel="Go to A/P aging" onGo={() => goReport('ap_aging_summary')}>
          <AgingMini base={base} report="ap_aging_summary" preset={p6} nounCol="Vendor" />
        </CardShell>
      </div>
    </div>
  );
}
